import "../stimulus_uses/use_outer_click.js"

/**
 * This is a generic controller for popup modals. It will show itself if a modal
 * target is connected and hide itself if it is disconnected.
 *
 * It expects the inner modal markup to be rendered server side and populated into
 * a turbo frame within the main controller element.
 *
 * It has only one expectation for the modal markup that is rendered server side:
 * The top level element should be the "modal" target. It's `popup_modal_target`
 * for ModalBuilder and `data-popup-modal-target="modal"` for simple html. E.g.
 * ```slim
 *   .modal data-popup-modal-target="modal"
 *    body...
 * ```
 *
 * Beyond that, the server is free to generate any markup it sees fit.
 *
 * For example, on the backend we have builder helpers and the modal body
 * is by default rendered with:
 * ```slim
 *   = popup.body do
 *     | Some content of modal body
 * ```
 *
 * Or you can use custom markup instead:
 * ```slim
 *   .modal-body.my.fancy.classes
 *     | Some content of modal body
 * ```
 *
 * Similarly, there's a `popup.header` helper that generates markup for the header.
 *
 * Full example:
 * ```slim
 *    = content_for_popup do |popup|
 *      = popup.header do # optional
 *        - if popup.in_modal?
 *          = popup.back_button sign_in_path
 *        = popup.title_tag t("front.bookings.chat.thank_you")
 *      = popup.body do
 *        anything
 * ```
 *
 * If you need to invoke a method on the controller, like for example the close
 * method, use the standard Stimulus `data-action` attribute. If you're using the
 * `Popup::ModalBuilder` it will generate the close button automatically.
 *
 * There two more actions which can be triggered when the modal is closing.
 * Both depend on presence of a specific stimulus target:
 * - `closeAction` target will cause it to check its data-value property and perform
 *   an action specified by it. See the code below for supported options.
 * - `closeAnimation` target will cause it to look for a data-close-animation-class
 *   property and will first attach that property to the target and then wait for
 *   `transitionend` event before actually closing the popup.
 *
 * For server side details see:
 * - front/app/views/front/login/_popup.slim
 * - Builder helpers in front/app/helpers/front/popup/
 */

stimulusApp.register(
  "popup-modal",
  class extends Stimulus.Controller {
    static targets = ["modal", "closeAction", "closeAnimation"]

    #shown = false
    #syncScheduled = false
    #syncFrameId = null

    connect() {
      this.#setupOuterClickHandling()
      this.#setupEscapeHandling()
    }

    // If the controller's own element is removed (e.g. together with an
    // ancestor, in one update), Stimulus calls this synchronously. Cancel any
    // frame scheduled by #scheduleSync so it doesn't later run against a
    // detached subtree and force the cleanup #hide() would have done.
    disconnect() {
      if (this.#syncFrameId !== null) {
        cancelAnimationFrame(this.#syncFrameId)
        this.#syncScheduled = false
        this.#syncFrameId = null
      }
      this.#hide()
    }

    isOpen() {
      return this.#shown
    }

    close() {
      if (
        this.hasCloseAnimationTarget &&
        this.closeAnimationTarget.dataset.closeAnimationClass
      ) {
        this.closeAnimationTarget.classList.add(
          this.closeAnimationTarget.dataset.closeAnimationClass,
        )

        if (
          getComputedStyle(this.closeAnimationTarget).animationDuration != "0s"
        ) {
          this.element.addEventListener("animationend", this.#closeAnimationEnd)
          return
        }
      }

      this.#closeModal()
    }

    modalTargetConnected() {
      this.#scheduleSync()
    }

    modalTargetDisconnected() {
      this.#scheduleSync()
    }

    // Connecting and disconnecting the modal target both resolve here, on the
    // next frame, instead of acting immediately. A turbo frame swapping its
    // content (e.g. form -> success) disconnects the old target and connects
    // the new one within the same update; by the time this runs, hasModalTarget
    // reflects the final state, so a same-frame swap is a no-op (`#shown` never
    // changes) instead of a spurious hide+show.
    #scheduleSync() {
      if (this.#syncScheduled) return
      this.#syncScheduled = true

      this.#syncFrameId = requestAnimationFrame(() => {
        this.#syncScheduled = false
        this.#syncFrameId = null
        if (this.hasModalTarget) {
          this.#show()
        } else {
          this.#hide()
        }
      })
    }

    #show() {
      if (this.#shown) return
      this.#shown = true

      this.element.dispatchEvent(
        new CustomEvent("hb:popup-before-open", {
          bubbles: true,
          cancelable: true,
        }),
      )

      HB.controlOffset.saveOffset()
      HB.fullscreenOverlay.beforeMount()
      this.element.style.display = "block"
    }

    #hide() {
      if (!this.#shown) return
      this.#shown = false

      HB.fullscreenOverlay.beforeUnmount()
      HB.controlOffset.restoreOffset()
      this.element.style.display = "none"

      this.element.dispatchEvent(
        new CustomEvent("hb:popup-closed", {
          bubbles: true,
          cancelable: true,
        }),
      )
    }

    #closeModal() {
      this.#processCloseAction()
      if (this.hasModalTarget) {
        this.modalTarget.remove() // Let modalTargetDisconnected callback handle hiding.
      }
    }

    #processCloseAction() {
      if (this.hasCloseActionTarget) {
        switch (this.closeActionTarget.dataset.value) {
          case "reload":
            document.location.reload()
            break
          case "reload_top":
            // used to prevent scroll position restoration
            const currentUrlWithoutHash =
              location.origin + location.pathname + location.search
            location.href = currentUrlWithoutHash
            break
          case "load_home_page":
            document.location = "/"
            break
        }
      }
    }

    #closeAnimationEnd = (event) => {
      if (event.target == this.closeAnimationTarget) {
        this.element.removeEventListener(
          "animationend",
          this.#closeAnimationEnd,
        )
        this.#closeModal()
      }
    }

    #setupOuterClickHandling() {
      const eventTargetIsOuter = (event) => {
        const list = event.target.classList
        return (
          list.contains("modal--overlay") ||
          list.contains("modal-container") ||
          list.contains("modal-outer")
        )
      }

      let mayBeStartedOnScrollbar = false

      // On desktop event probably happened on scrollbar.
      // "Probably" to avoid complexity of checking actual scrollbar presence.
      // LTR and RTL
      const mayBeScrollbar = (event) => {
        if (HB.isMobile) {
          return false
        }
        const viewportWidth = document.documentElement.clientWidth
        const eventX = event.clientX || event.changedTouches?.[0].clientX
        if (!eventX) {
          return false
        }
        const SCROLLBAR_WIDTH = 17 // approx
        return (
          eventX >= viewportWidth - SCROLLBAR_WIDTH || eventX <= SCROLLBAR_WIDTH
        )
      }

      HB.useOuterClick(this, {
        listenerTarget: this.element,
        isOuterEvent: eventTargetIsOuter,
        shouldStart: (event) => {
          mayBeStartedOnScrollbar = mayBeScrollbar(event)
          return !(
            this.hasModalTarget && this.modalTarget.dataset.disableOuterClick
          )
        },
        shouldFinish: (event) =>
          !mayBeScrollbar(event) && !mayBeStartedOnScrollbar,
        preventDefault: true,
        onClose: () => this.close(),
      })
    }

    #setupEscapeHandling() {
      document.addEventListener("keyup", (event) => {
        // fullscreen class check is to avoid clashing with Photoswipe controls when a photo gallery is open
        if (
          event.key == "Escape" &&
          this.#shown &&
          !document.documentElement.classList.contains("fullscreen")
        ) {
          event.preventDefault()
          this.close()
        }
      })
    }
  },
)
